import { format } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Container } from '../components/Animations'
import { PrimaryButton, SecondaryButton } from '../components/Button'
import { TextArea, TextInputWithLabel, InputLabel } from '../components/Input'
import PageHeader from '../components/PageHeader'
import Pagination from '../components/Pagination'
import Table from '../components/Table'
import TimeText from '../components/TimeText'
import {
  ListContent,
  ListPage,
  PageActionRow,
  PageHeaderRow
} from '../components/DashboardPrimitives'
import Radom, { ISupportTicket, ISupportChannelCode } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { errorToast, successToast } from '../util/Util'
import { StatusBadge, IStatusBadge } from './CheckoutSessions'

const PAGE_SIZE = 25

const Panel = styled.div`
  background-color: white;
  border: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 640px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
`

const TicketIdShort = styled.span`
  font-family: monospace;
  font-size: 12px;
  color: ${RADOM_COLORS.GRAY_DARKER};
  background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  border: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-radius: 5px;
  padding: 2px 6px;
`

const PanelTitle = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${RADOM_COLORS.BLACK};
`

const PanelText = styled.span`
  font-size: 13px;
  line-height: 1.5;
  color: ${RADOM_COLORS.GRAY_DARKER};
`

const CodeBox = styled.div`
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 3px;
  padding: 14px 18px;
  border-radius: 8px;
  background-color: ${RADOM_COLORS.GRAY_MED};
  color: ${RADOM_COLORS.BLACK};
  width: fit-content;
  user-select: all;
`

export function supportStatusBadge (status: string): React.ReactElement {
  let badgeStatus: IStatusBadge = 'processing'
  let label = 'Open'
  if (status === 'awaiting_reply') {
    badgeStatus = 'pending'
    label = 'Awaiting your reply'
  }
  if (status === 'resolved') {
    badgeStatus = 'success'
    label = 'Resolved'
  }
  return <StatusBadge status={badgeStatus}>{label}</StatusBadge>
}

const Support = observer(() => {
  const navigate = useNavigate()

  const [tickets, setTickets] = useState([] as ISupportTicket[])
  const [total, setTotal] = useState(0)
  const [curPage, setCurPage] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const [showCreate, setShowCreate] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const [showChannelAuth, setShowChannelAuth] = useState(false)
  const [channelCode, setChannelCode] = useState<ISupportChannelCode | null>(null)
  const [isGeneratingCode, setIsGeneratingCode] = useState(false)

  const loadTickets = (page: number): void => {
    setIsLoading(true)
    Radom.listSupportTickets({ limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then(res => {
        setTickets(res.tickets)
        setTotal(res.total)
      })
      .catch(err => errorToast(err.response?.data?.error || err.message))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    loadTickets(curPage)
  }, [curPage])

  const onCreateTicket = async (): Promise<void> => {
    if (!subject.trim() || !body.trim()) {
      errorToast('Subject and message are required')
      return
    }
    setIsCreating(true)
    try {
      const ticket = await Radom.createSupportTicket(subject.trim(), body.trim())
      successToast('Support ticket created')
      navigate(`/support/${ticket.id}`)
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message)
    } finally {
      setIsCreating(false)
    }
  }

  const onGenerateCode = async (): Promise<void> => {
    setIsGeneratingCode(true)
    try {
      setChannelCode(await Radom.createSupportChannelCode())
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message)
    } finally {
      setIsGeneratingCode(false)
    }
  }

  return <Container>
    <ListPage>
      <PageHeaderRow>
        <PageHeader title='Support' subTitle='Get help from Radom support' />
        <PageActionRow>
          <SecondaryButton onClick={() => {
            setShowChannelAuth(s => !s)
            setShowCreate(false)
          }}>
            Authorize a channel
          </SecondaryButton>
          <PrimaryButton style={{ gap: 8 }} onClick={() => {
            setShowCreate(s => !s)
            setShowChannelAuth(false)
          }}>
            <span style={{ fontSize: 15, lineHeight: 1 }}>+</span>
            <span>New ticket</span>
          </PrimaryButton>
        </PageActionRow>
      </PageHeaderRow>

      {showCreate && <Panel>
        <PanelTitle>Create a support ticket</PanelTitle>
        <TextInputWithLabel
          label='Subject'
          value={subject}
          placeholder='Short summary of your issue'
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <InputLabel>Message</InputLabel>
          <TextArea
            rows={5}
            value={body}
            placeholder='Describe your issue in detail'
            onChange={e => setBody(e.target.value)}
          />
        </div>
        <div>
          <PrimaryButton isLoading={isCreating} onClick={() => { onCreateTicket() }}>
            Submit ticket
          </PrimaryButton>
        </div>
      </Panel>}

      {showChannelAuth && <Panel>
        <PanelTitle>Authorize a Telegram or Slack conversation</PanelTitle>
        <PanelText>
          When you contact Radom support through Telegram or Slack, our assistant cannot
          see which account the conversation belongs to. Generate a one-time code below and
          paste it into the support conversation to securely link it to this organization.
          The code is single use and expires after 15 minutes.
        </PanelText>
        {channelCode
          ? <>
            <CodeBox>{channelCode.code}</CodeBox>
            <PanelText>
              Paste this code into your Telegram or Slack support conversation.
              It expires at {format(new Date(channelCode.expiresAt), 'h:mm a')}.
            </PanelText>
          </>
          : null}
        <div>
          <SecondaryButton isLoading={isGeneratingCode} onClick={() => { onGenerateCode() }}>
            {channelCode ? 'Generate a new code' : 'Generate code'}
          </SecondaryButton>
        </div>
      </Panel>}

      <ListContent>
        <Table
          borderless
          headers={['Ticket', 'Subject', 'Status', 'Source', 'Created', 'Last update'].map(h => <span key={h}>{h}</span>)}
          isLoading={isLoading}
          emptyPlaceholderText='No support tickets yet — create one to get help from our team'
          onClickRow={i => navigate(`/support/${tickets[i].id}`)}
          items={tickets.map(ticket => [
            <TicketIdShort key='id'>{ticket.id.slice(0, 8)}</TicketIdShort>,
            <span key='subject' style={{ fontWeight: 500 }}>{ticket.subject}</span>,
            <div key='status'>{supportStatusBadge(ticket.status)}</div>,
            <span key='source' style={{ textTransform: 'capitalize' }}>{ticket.source}</span>,
            <TimeText key='created' date={new Date(ticket.createdAt)} />,
            <TimeText key='updated' date={new Date(ticket.updatedAt)} />
          ])}
        />
        <Pagination
          curPage={curPage}
          numPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
          onPageClick={page => setCurPage(page)}
        />
      </ListContent>
    </ListPage>
  </Container>
})

export default Support
